import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { MessageSquare, Calendar } from 'lucide-react';
import { authAPI } from '../api/client';
import Layout from '../components/Layout';
import '../styles/user-profile.css';

function getInitials(name) {
  if (!name) return '?';
  const parts = name.trim().split(' ');
  return (parts.length > 1 ? parts[0][0] + parts[parts.length - 1][0] : name[0]).toUpperCase();
}

function formatDate(d) {
  if (!d) return '';
  return new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

export default function UserProfile() {
  const { userId } = useParams();
  const navigate = useNavigate();
  const currentUserId = localStorage.getItem('userId');
  const [user, setUser] = useState(null);
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (userId === currentUserId) { navigate('/profile', { replace: true }); return; }
    Promise.all([
      authAPI.getUser(userId),
      authAPI.getUserActivities(userId),
    ]).then(([uRes, aRes]) => {
      setUser(uRes.data);
      setActivities(aRes.data || []);
    }).catch(() => {
      setUser(null);
    }).finally(() => setLoading(false));
  }, [userId]);

  if (loading) return (
    <Layout title="Profile">
      <div className="up-loading">Loading…</div>
    </Layout>
  );

  if (!user) return (
    <Layout title="Profile">
      <div className="up-loading">User not found.</div>
    </Layout>
  );

  return (
    <Layout title={user.display_name || 'Profile'}>
      <div className="up-page">

        {/* Hero card */}
        <div className="up-hero-card">
          <div className="up-hero-banner" />
          <div className="up-hero-body">
            <div className="up-avatar-wrap">
              {user.avatar_url
                ? <img src={user.avatar_url} alt="" className="up-avatar-img" />
                : <div className="up-avatar-initials">{getInitials(user.display_name)}</div>}
            </div>
            <div className="up-hero-info">
              <h1 className="up-name">{user.display_name}</h1>
              {user.bio && <p className="up-bio">{user.bio}</p>}
            </div>
            <button className="up-msg-btn" onClick={() => navigate(`/messages/${userId}`)}>
              <MessageSquare size={16} strokeWidth={2.5} />
              Message
            </button>
          </div>
        </div>

        {/* Upcoming plans */}
        <div className="up-card">
          <div className="up-card-header">
            <Calendar size={15} strokeWidth={2.5} />
            <span className="up-card-title">Upcoming Plans</span>
            {activities.length > 0 && <span className="up-card-count">{activities.length}</span>}
          </div>
          {activities.length === 0 ? (
            <p className="up-empty">No upcoming public plans.</p>
          ) : (
            <div className="up-act-list">
              {activities.map(a => (
                <div key={a.id} className="up-act-row" onClick={() => navigate(`/activity/${a.id}`)}>
                  <div className="up-act-dot" />
                  <div className="up-act-info">
                    <span className="up-act-title">{a.title || a.activity_type}</span>
                    <span className="up-act-meta">
                      {formatDate(a.start_time)}
                      {(a.location_name || a.location_fuzzy_area) ? ` · ${a.location_name || a.location_fuzzy_area}` : ''}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

      </div>
    </Layout>
  );
}
